import { RecordSheet } from '@/components/record-sheet/RecordSheet'
import { RecordSheetContext } from '@/components/record-sheet/context'
import { type ReactNode, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

interface SheetEntry {
  key: string
  id: string
}

const PARAM = 'sheet'

function parseStack(raw: string | null): SheetEntry[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((part) => {
      const [key, id] = part.split(':')
      return key && id ? { key, id } : null
    })
    .filter((e): e is SheetEntry => e !== null)
}

/**
 * Owns the stack of open record sheets. The stack lives in `?sheet=` so a
 * sheet (or a sheet opened from inside another sheet) is a shareable link and
 * the browser back button closes the top one.
 */
export function RecordSheetProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams()
  const stack = useMemo(() => parseStack(params.get(PARAM)), [params])

  const writeStack = useCallback(
    (next: SheetEntry[]) => {
      setParams((prev) => {
        const out = new URLSearchParams(prev)
        if (next.length) out.set(PARAM, next.map((e) => `${e.key}:${e.id}`).join(','))
        else out.delete(PARAM)
        return out
      })
    },
    [setParams]
  )

  const openRecord = useCallback(
    (key: string, id: string | number) => {
      const entry = { key, id: String(id) }
      const top = stack[stack.length - 1]
      if (top && top.key === entry.key && top.id === entry.id) return
      writeStack([...stack, entry])
    },
    [stack, writeStack]
  )

  const value = useMemo(() => ({ openRecord }), [openRecord])

  return (
    <RecordSheetContext.Provider value={value}>
      {children}
      {stack.map((entry, index) => (
        <RecordSheet
          key={`${entry.key}:${entry.id}:${index}`}
          resourceKey={entry.key}
          id={entry.id}
          onClose={() => writeStack(stack.slice(0, index))}
        />
      ))}
    </RecordSheetContext.Provider>
  )
}

